import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV, LOGO_SRC } from "../lib/content";

const Wordmark = ({ dark = false, className = "" }) => (
  <img
    src={LOGO_SRC}
    alt="Jason Howard Photography"
    width="156"
    height="40"
    decoding="async"
    className={`h-10 md:h-12 w-auto object-contain ${dark ? "" : "invert"} ${className}`}
  />
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const openMenu = () => {
    setOpen(true);
    requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
  };
  const closeMenu = () => {
    setShown(false);
    setTimeout(() => setOpen(false), 350);
  };

  useEffect(() => {
    setShown(false);
    setOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onHero = location.pathname === "/" && !scrolled;

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled ? "backdrop-blur-xl bg-paper/80 border-b border-ink/5 py-3" : "bg-transparent py-5"
        }`}
      >
        <nav className="max-w-[1500px] mx-auto px-5 md:px-10 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" aria-label="Jason Howard Photography home" className="flex items-center group">
            <Wordmark dark={onHero} />
          </Link>

          <div className="hidden lg:flex items-center gap-9">
            {NAV.slice(1, -1).map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`link-underline text-[11px] uppercase tracking-[0.22em] transition-colors ${
                  onHero
                    ? "text-paper/80 hover:text-paper"
                    : location.pathname === item.to
                    ? "text-ink"
                    : "text-ink-soft hover:text-ink"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/contact/"
              data-testid="nav-check-availability"
              className={`hidden sm:inline-flex items-center border text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 rounded-full transition-colors duration-500 ${
                onHero
                  ? "border-paper/50 text-paper hover:bg-paper hover:text-ink"
                  : "border-ink text-ink hover:bg-ink hover:text-paper"
              }`}
            >
              Check Availability
            </Link>
            <button
              onClick={openMenu}
              data-testid="nav-menu-open"
              className={`lg:hidden p-2 -mr-2 ${onHero ? "text-paper" : "text-ink"}`}
              aria-label="Open menu"
            >
              <Menu size={24} strokeWidth={1.4} />
            </button>
          </div>
        </nav>
      </header>

      {open && (
        <div
          className={`nav-menu ${shown ? "nav-menu-open" : ""} fixed inset-0 z-[60] bg-paper flex flex-col`}
          data-testid="mobile-menu"
        >
          <div className="flex items-center justify-between px-5 py-5">
            <Wordmark />
            <button onClick={closeMenu} data-testid="nav-menu-close" className="p-2 -mr-2" aria-label="Close menu">
              <X size={26} strokeWidth={1.4} />
            </button>
          </div>
          <div className="flex-1 flex flex-col justify-center px-7 gap-1">
            {NAV.map((item, i) => (
              <div key={item.to} className="nav-menu-item" style={{ transitionDelay: `${0.1 + i * 0.06}s` }}>
                <Link
                  to={item.to}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="block font-serif text-5xl sm:text-6xl text-ink py-2"
                >
                  {item.label}
                </Link>
              </div>
            ))}
          </div>
          <div className="px-7 pb-10 text-ink-soft text-sm tracking-wide">
            <a href="tel:+447766257356" className="link-underline">07766 257356</a>
            <span className="mx-3 text-ink/20">/</span>
            <a href="mailto:info@jasonhowardphotography.co.uk" className="link-underline">Email</a>
          </div>
        </div>
      )}
    </>
  );
};
